import { useRef, useState } from "react";
import { IconButton } from "@/components/common/IconButton";
import { ContextMenu, type ContextMenuGroup } from "@/components/common/ContextMenu";
import { buildFilePathBreadcrumbs } from "@/lib/file-path-breadcrumbs";
import { saveAllDirtyFiles } from "@/lib/save-all";
import { useChatStore } from "@/stores/useChatStore";
import { useEditorPrefsStore } from "@/stores/useEditorPrefsStore";
import { useEditorSettingsStore } from "@/stores/useEditorSettingsStore";

interface FileHeaderBarProps {
	/** ワークスペースルートからの相対パス。 */
	path: string;
	/** パンくずの親ディレクトリを選択したときに呼ばれる。 */
	onNavigateToDirectory: (path: string) => void;
	dirty: boolean;
	/** 保存リクエストの送信中。true の間は「保存中…」を表示し、保存/破棄メニュー項目を無効化する。 */
	saving: boolean;
	/** true のとき右端に "..." メニュー (保存/破棄) を表示する。編集不可なファイルでは false。 */
	showMenu: boolean;
	/** マークダウン・CSV・HTML のときだけ Preview / Source トグルを表示する。 */
	viewMode?: "preview" | "source";
	onViewModeChange?: (mode: "preview" | "source") => void;
	/** テキストエディタを表示中のときだけ "..." メニューに行折り返しの切り替えを載せる。 */
	showWordWrapToggle?: boolean;
	onSave: () => void;
	onDiscardRequest: () => void;
}

/** パンくず行の高さ。12px の文字に上下 4px ずつの余白を残す。 */
const BAR_HEIGHT = 20;

/** VSCode 風のタブ直下ヘッダー。パス表示・md/csv/html の Preview/Source 切替・保存/破棄メニューを持つ。 */
export function FileHeaderBar({
	path,
	onNavigateToDirectory,
	dirty,
	saving,
	showMenu,
	viewMode,
	onViewModeChange,
	showWordWrapToggle,
	onSave,
	onDiscardRequest,
}: FileHeaderBarProps) {
	const [menuOpen, setMenuOpen] = useState(false);
	const selectedTreeNodePath = useChatStore((state) => state.selectedEnvironmentFilesTreeNodePath);
	const kebabRef = useRef<HTMLButtonElement>(null);
	const anyDirty = useChatStore((state) => state.dirtyFileIds.length > 0);
	const breadcrumbs = buildFilePathBreadcrumbs(path);
	const autoSaveMode = useEditorSettingsStore((state) => state.autoSaveMode);
	const setAutoSaveMode = useEditorSettingsStore((state) => state.setAutoSaveMode);
	const wordWrap = useEditorPrefsStore((state) => state.wordWrap);
	const toggleWordWrap = useEditorPrefsStore((state) => state.toggleWordWrap);
	const editMenuGroups: readonly ContextMenuGroup[] = [
		{
			items: [
				{
					label: "ファイルを保存",
					shortcut: "⌘S",
					disabled: !dirty || saving,
					testId: "file-header-menu-save",
					onSelect: onSave,
				},
				{
					label: "すべて保存",
					shortcut: "⇧⌘S",
					disabled: !anyDirty || saving,
					testId: "file-header-menu-save-all",
					onSelect: () => void saveAllDirtyFiles(),
				},
				{
					label: "編集を破棄",
					destructive: true,
					disabled: !dirty || saving,
					testId: "file-header-menu-discard",
					onSelect: onDiscardRequest,
				},
			],
		},
		{
			label: "自動保存",
			items: [
				{
					key: "auto-save-off",
					label: "オフ",
					selectable: true,
					selected: autoSaveMode === "off",
					testId: "file-header-menu-autosave-off",
					onSelect: () => setAutoSaveMode("off"),
				},
				{
					key: "auto-save-after-delay",
					label: "一定時間後",
					selectable: true,
					selected: autoSaveMode === "afterDelay",
					testId: "file-header-menu-autosave-after-delay",
					onSelect: () => setAutoSaveMode("afterDelay"),
				},
				{
					key: "auto-save-on-focus-change",
					label: "フォーカス喪失時",
					selectable: true,
					selected: autoSaveMode === "onFocusChange",
					testId: "file-header-menu-autosave-on-focus-change",
					onSelect: () => setAutoSaveMode("onFocusChange"),
				},
			],
		},
	];
	const viewMenuGroups: readonly ContextMenuGroup[] = [
		{
			label: "表示",
			items: [
				{
					label: "行を折り返す",
					shortcut: "⌥Z",
					selectable: true,
					selected: wordWrap,
					testId: "file-header-menu-word-wrap",
					onSelect: toggleWordWrap,
				},
			],
		},
	];
	const menuItems: readonly ContextMenuGroup[] = [
		...(showMenu ? editMenuGroups : []),
		...(showWordWrapToggle ? viewMenuGroups : []),
	];

	return (
		<div
			data-testid="file-header-bar"
			style={{
				display: "flex",
				alignItems: "center",
				height: BAR_HEIGHT,
				flexShrink: 0,
				padding: "0 4px 0 12px",
				background: "var(--editor-bg)",
				gap: 8,
			}}
		>
			<div
				style={{
					flex: 1,
					minWidth: 0,
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					fontSize: "var(--text-caption)",
					lineHeight: "var(--leading-none)",
				}}
			>
				{breadcrumbs.map((breadcrumb, index) => (
					<span key={breadcrumb.path}>
						{index > 0 && <span style={{ color: "var(--fg-3)" }}> {"›"} </span>}
						{breadcrumb.isCurrent ? (
							<span style={{ color: "var(--fg-1)" }}>{breadcrumb.label}</span>
						) : (
							<button
								type="button"
								title={breadcrumb.path}
								data-tooltip-nowrap
								data-testid={`file-header-breadcrumb-${breadcrumb.path}`}
								onClick={() => onNavigateToDirectory(breadcrumb.path)}
								data-selected={selectedTreeNodePath === breadcrumb.path}
								className="cursor-pointer text-fg-2 hover:text-fg-0 data-[selected=true]:text-fg-0"
							>
								{breadcrumb.label}
							</button>
						)}
					</span>
				))}
			</div>
			{saving && (
				<span
					data-testid="file-header-saving"
					style={{ flexShrink: 0, fontSize: "var(--text-caption)", color: "var(--fg-3)" }}
				>
					保存中…
				</span>
			)}
			{viewMode && (
				<div style={{ display: "inline-flex", flexShrink: 0, gap: 2 }}>
					{(["preview", "source"] as const).map((mode) => (
						<button
							key={mode}
							type="button"
							data-testid={`file-header-viewmode-${mode}`}
							onClick={() => onViewModeChange?.(mode)}
							style={{
								padding: "2px 8px",
								fontSize: "var(--text-caption)",
								lineHeight: "var(--leading-none)",
								fontWeight: viewMode === mode ? 500 : 200,
								color: viewMode === mode ? "var(--fg-1)" : "var(--fg-3)",
								background: viewMode === mode ? "var(--row-selected-bg)" : "transparent",
								border: "none",
								borderRadius: "var(--radius-2)",
								cursor: "pointer",
								fontFamily: "inherit",
							}}
						>
							{mode === "preview" ? "プレビュー" : "ソース"}
						</button>
					))}
				</div>
			)}
			{(showMenu || showWordWrapToggle) && (
				<>
					<IconButton
						ref={kebabRef}
						icon="kebab"
						size={BAR_HEIGHT}
						iconSize={14}
						data-testid="file-header-menu-button"
						onClick={() => setMenuOpen((v) => !v)}
					/>
					{menuOpen && kebabRef.current && (
						<ContextMenu
							position={{ anchor: kebabRef.current }}
							onClose={() => setMenuOpen(false)}
							testId="file-header-menu"
							items={menuItems}
						/>
					)}
				</>
			)}
		</div>
	);
}
